// 全体像 PDF / サムネイルに描画される「drawable」オブジェクトの判定（単一の正本）。
//
// 「comment-thread はビューポート固定の UI ピンであり、エクスポート/サムネには描画しない」という不変条件を、
// web（pdf-export / thumbnail / 自動保存の空判定）と api（export-url の空判定 hasDrawableObject）の双方で共有する。
// 同一条件を各所に literal で重複させると、将来 comment-thread 以外の非描画 type を増やしたときに追従漏れで
// client / server の空判定がずれ（保存済みでも 404／空でも 200）得るため、ここを唯一の正本とする。非描画 type を
// 増やす場合は必ず NON_DRAWABLE_OBJECT_TYPES へ追加し、全参照箇所の挙動を確認すること。
//
// 用途は「PDF / サムネへ描画されるか」の判定に限定する。コネクタ端点資格・コメント操作・UI 操作制限などは
// 意味が異なるため、それぞれの専用判定（isConnectorEndpointObject 等）を使い、この関数を流用しないこと。

import type { CanvasOp } from "./canvas-op";

/** 全体像 PDF / サムネイルに描画しないオブジェクト種別（ビューポート固定の UI）。 */
export const NON_DRAWABLE_OBJECT_TYPES: readonly string[] = ["comment-thread"];

/**
 * 全体像 PDF / サムネイルに描画される drawable かを判定する。
 * type が未知（undefined・DynamoDB の射影欠落・旧データ等）の場合は false（描画対象としない＝保守的）。
 */
export function isDrawableObject(type: string | undefined): boolean {
  return type !== undefined && !NON_DRAWABLE_OBJECT_TYPES.includes(type);
}

/**
 * オブジェクト一覧のうち drawable（全体像 PDF / サムネイルに描画される）件数を数える。
 * META の drawableObjectCount（export-url の空判定を META 1 回読みで済ませる集計値）の
 * 初期化・追従に使う「実数の数え上げ」の正本。判定は isDrawableObject と完全に一致させ、
 * カウンタと個々の増減判定がずれないようにする（type 未知は非描画＝保守的に数えない）。
 */
export function countDrawableObjects(
  objects: ReadonlyArray<{ readonly type?: string | undefined }>
): number {
  // drawable 判定（isDrawableObject）を満たすオブジェクトだけを 1 件ずつ数え上げる
  // （中間配列を作る filter().length ではなく、単一走査の畳み込みで数える）。
  return objects.reduce((count, object) => (isDrawableObject(object.type) ? count + 1 : count), 0);
}

/**
 * 単一の op が全体像 PDF（export スナップショット）へ影響し得るかを、対象オブジェクトの現在型を参照せずに判定する。
 *
 * `comment.message.*` / `canvas.meta.update` は PDF 内容に影響しないため false。これにより、描画後〜finalize の間に
 * コメント追加やタイトル変更だけが入った場合に、export を「世代が進んだ＝stale」と誤判定して再保存を空転させるのを
 * 防ぐ。それ以外の drawable 操作（create / delete / transform / text / style / pen / 非 blob の attachment 差し替え）は
 * 影響し得るとして true を返す。`object.delete` 等は対象が実際に drawable かをここでは型参照せず保守的に true とする
 * （comment-thread への transform 等は厳密には非影響だが、false 側に倒すと drawable 変更を取りこぼす方が危険なため
 * over-detect 側に倒す。多くても再保存 1 回ぶんの空転に収まる）。`object.create` はインラインの object.type で、
 * `object.attachment.update` は blob: の本人用プレビュー（永続化されない）を除外して判定する。
 *
 * web の `inFlightOpsAffectExport`（楽観 in-flight の export ゲート）と api の finalize 世代ゲート（描画後に export 影響
 * op が確定したか）で同義になるよう、判定をここに集約する。
 */
export function opMayAffectExportSnapshot(op: CanvasOp): boolean {
  switch (op.type) {
    case "object.create":
      return isDrawableObject(op.object.type);
    case "pen.stroke.commit":
    case "object.delete":
    case "object.transform":
    case "object.text.update":
    case "object.text.style.update":
    case "object.style.update":
    // 表のセル本文・行列構成はいずれも描画結果へ現れるため、export スナップショットへ影響し得る。
    case "object.table.cell.update":
    case "object.table.shape.update":
    // 本文中の表の列幅は PDF・サムネイルの列割りと折り返しを変えるため、同じく影響し得る。
    case "object.table.columns.update":
      return true;
    case "object.attachment.update":
      return !op.src.startsWith("blob:");
    case "canvas.meta.update":
      // サイズ変更は全体像 PDF / サムネの出力範囲（キャンバス矩形）を変えるため export へ影響する。
      // タイトル / サムネキーの変更は PDF 内容に影響しないので従来どおり非影響のまま。
      return op.sizePreset !== undefined;
    case "comment.message.add":
    case "comment.message.update":
    case "comment.message.delete":
      return false;
    default: {
      // 網羅性ガード。CanvasOp に新 op が増えたとき、ここで型エラーになって追従漏れを検知する。
      const exhaustiveCheck: never = op;
      void exhaustiveCheck;
      return false;
    }
  }
}
